import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { siteImages, CONTACT_INFO, RETURN_ADDRESS } from '../data/mock';
import { RotateCcw, CheckCircle, XCircle, Phone, Mail, MapPin, Heart, ArrowRight } from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';

const ReturnsPage = () => {
  usePageTitle(
    'Returns & Refund Policy',
    '30-day return policy at Stitch Your Dash. Easy returns for unused items in original packaging.'
  );

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[64px] md:pt-[105px] lg:pt-[125px]">
        {/* Banner */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img src={siteImages.returnsBanner} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 gradient-banner-overlay" />
          </div>
          <div className="relative container-wide py-20 lg:py-28 text-cream-50">
            <p className="eyebrow text-rose-200 mb-4" style={{color: '#f5d2c6'}}><Heart className="w-3.5 h-3.5" /> Hassle-free Policy</p>
            <h1 className="font-display text-editorial-lg leading-[1.05] max-w-3xl">
              Returns & <em className="italic-display text-rose-200 not-italic" style={{fontStyle:'italic'}}>refunds</em>.
            </h1>
            <p className="mt-5 max-w-xl text-cream-200/85">
              30-day, no-stress returns on unused items — your peace of mind, every time.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
            {/* Return Address Highlight */}
            <div className="bg-rose-50 rounded-[32px] p-7 lg:p-9 border border-rose-100 mb-8">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-rose-500 text-white flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <p className="eyebrow mb-2">Send Returns To</p>
                  <h2 className="font-display text-2xl lg:text-3xl text-warmBrown-950 mb-2">Return Address</h2>
                  <p className="text-warmBrown-800 leading-relaxed">
                    {RETURN_ADDRESS.street}<br />
                    {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                    {RETURN_ADDRESS.country}
                  </p>
                </div>
              </div>
            </div>

            {/* Policy */}
            <div className="bg-warmWhite-100 rounded-[32px] p-7 lg:p-10 border border-cream-200 shadow-card mb-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-warmBrown-900 text-cream-50 flex items-center justify-center">
                  <RotateCcw className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="font-display text-2xl lg:text-3xl text-warmBrown-950">30-Day Return Policy</h2>
                  <p className="text-warmBrown-500">Stress-free returns within 30 days of delivery</p>
                </div>
              </div>
              <p className="text-warmBrown-700 leading-relaxed mb-7">
                We want you to be completely happy with every Stitch Your Dash purchase. If something isn’t quite right, you can return most items within 30 days of delivery for a full refund or exchange.
              </p>

              <div className="grid md:grid-cols-2 gap-5">
                <div className="bg-sage-50 rounded-2xl p-6 border border-sage-100">
                  <h3 className="font-display text-lg text-warmBrown-950 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-sage-600" /> Eligible for Return
                  </h3>
                  <ul className="space-y-1.5 text-warmBrown-700 text-sm">
                    <li>• Items in original condition</li>
                    <li>• Unused, unopened items</li>
                    <li>• Items in original packaging</li>
                    <li>• With receipt or proof of purchase</li>
                  </ul>
                </div>
                <div className="bg-rose-50 rounded-2xl p-6 border border-rose-100">
                  <h3 className="font-display text-lg text-warmBrown-950 mb-3 flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-rose-600" /> Not Eligible
                  </h3>
                  <ul className="space-y-1.5 text-warmBrown-700 text-sm">
                    <li>• Digital products</li>
                    <li>• Final-sale items</li>
                    <li>• Used or opened items</li>
                    <li>• Items past the 30-day window</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="bg-warmWhite-100 rounded-[32px] p-7 lg:p-10 border border-cream-200 shadow-card mb-8">
              <h2 className="font-display text-2xl text-warmBrown-950 mb-6">How to Initiate a Return</h2>
              <ol className="space-y-5">
                {[
                  { t: 'Reach Out', d: `Email ${CONTACT_INFO.email} or call ${CONTACT_INFO.phone} with your order number.` },
                  { t: 'Get a Return Label', d: 'We’ll email you a prepaid return shipping label.' },
                  { t: 'Ship It Back', d: 'Pack the item securely and drop it off at your nearest carrier.' },
                  { t: 'Receive Your Refund', d: 'Refunds are processed within 5–7 business days of receiving the return.' },
                ].map((step, idx) => (
                  <li key={idx} className="flex items-start gap-4">
                    <span className="w-9 h-9 rounded-full bg-rose-500 text-white font-display text-sm flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                    <div>
                      <h4 className="font-display text-lg text-warmBrown-950">{step.t}</h4>
                      <p className="text-warmBrown-700 text-sm leading-relaxed">{step.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* CTA */}
            <div className="bg-warmBrown-900 text-cream-50 rounded-[32px] p-8 lg:p-10 text-center">
              <h3 className="font-display text-2xl lg:text-3xl mb-5">
                Need help with a <em className="italic-display text-rose-300 not-italic" style={{fontStyle:'italic'}}>return</em>?
              </h3>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 text-rose-300 hover:text-rose-200 transition-colors">
                  <Phone className="w-5 h-5" /> {CONTACT_INFO.phone}
                </a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 text-rose-300 hover:text-rose-200 transition-colors">
                  <Mail className="w-5 h-5" /> {CONTACT_INFO.email}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ReturnsPage;
